'use client';

import { type ReactNode } from 'react';
import { useMedia } from 'react-use';

import clsx from 'clsx';
import NextImage from 'next/image';

import { bannerLinkTypes, breakpoints } from '@/app.config';

import type { Banner } from '@/api/types/home.types';

import { ForwardedLink } from '@/components/ForwardedLink';
import { Image } from '@/components/Image';

import styles from '@/views/HomeView/BannerSlider/BannerSlide.module.scss';

// ----------------------------------------------------------------

const DESKTOP_ASPECT_RATIO = 984 / 552;
const MOBILE_ASPECT_RATIO = 311 / 311;

// ----------------------------------------------------------------

interface BannerSlideProps {
  children?: ReactNode;
  desktopBanner: string;
  mobileBanner: string;
  banner: Banner;
}

// ----------------------------------------------------------------

export const BannerSlide = (props: BannerSlideProps) => {
  const { children, desktopBanner, mobileBanner } = props;

  const { linkType, link, moduleId, title } = props.banner;

  const isLinkTypeOfCollection = linkType === bannerLinkTypes.collection;
  const isLinkTypeOfExternal = linkType === bannerLinkTypes.externalLink;

  let url: string;
  if (isLinkTypeOfExternal) {
    url = link;
  } else if (isLinkTypeOfCollection) {
    url = '';
  } else {
    url = `/watch/${moduleId}/${link}`;
  }

  const isDesktop = useMedia(breakpoints.small, true);

  return (
    <ForwardedLink href={url} className={styles.bannerSlide}>
      <Image
        aspectRatio={isDesktop ? DESKTOP_ASPECT_RATIO : MOBILE_ASPECT_RATIO}
        priority
        fill
        src={isDesktop ? desktopBanner : mobileBanner}
        alt={title}
        quality={75}
        wrapperClassName={styles.imageWrapper}
        sizes={`100vw, ${breakpoints.small} 33vw, ${breakpoints.large} 57vw, ${breakpoints.extraLarge} 100vw`}
      >
        {children}
      </Image>
    </ForwardedLink>
  );
};

// ----------------------------------------------------------------

export const BannerSlideSkelton = () => {
  return (
    <div className={clsx(styles.bannerSlide, styles.skelton)}>
      <div className={styles.imageWrapper}>
        <NextImage
          width={984}
          height={552}
          src="/assets/images/banner-placeholder.png"
          alt="banner-card-skelton"
          className={styles.bannerImage}
          quality={75}
          sizes={`100vw, ${breakpoints.small} 33vw, ${breakpoints.large} 57vw, ${breakpoints.extraLarge} 100vw`}
        />
      </div>
    </div>
  );
};
